import React, { useState, useRef, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import "./CriteriaManagement.css";
import MainLayout from "../Common/MainLayout";
import { useLocation } from "react-router-dom";
import httpService from "../../services/httpService";
import { fetchAuthSession } from "aws-amplify/auth";

const EditMinimumProviders = () => {
  const location = useLocation();
  const { criteriaId, criteriaName } = location.state || {};

  const [drivingTime, setdrivingTimeData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const toast = useRef(null);

  const fetchDrivingTimeData = async () => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "ProviderDetails",
          id_criteria: criteriaId,
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        const DrivingTimeData = response.data.TimeAndDistance.map((item) => ({
          idn_criteria_spec: item.idn_criteria_spec,
          CountyCode: item.countycode,
          County: item.county,
          Type: item.countytype,
          Zone: item.regionname,
          MinimumProviders: item.minproviders,
          DrivingTime: item.tme_drve,
          Distance: item.distance,
        }));
        setdrivingTimeData(DrivingTimeData);
      })
      .catch((error) => {
        console.error("Error fetching minimum providers:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchInitialData = async () => {
    setLoading(true);
    await fetchDrivingTimeData();
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Pending filter selections (before Apply is clicked)
  const [pendingTypeFilter, setPendingTypeFilter] = useState([]);
  const [pendingZoneFilter, setPendingZoneFilter] = useState([]);
  
  // Applied filters (after Apply is clicked)
  const [appliedTypeFilter, setAppliedTypeFilter] = useState([]);
  const [appliedZoneFilter, setAppliedZoneFilter] = useState([]);

  const [masterCheckbox, setMasterCheckbox] = useState(false);

  // Handle Apply Filter button click
  const handleApplyFilters = () => {
    setAppliedTypeFilter(pendingTypeFilter);
    setAppliedZoneFilter(pendingZoneFilter);
  };

  // Handle Clear Filter button click
  const handleClearFilters = () => {
    setPendingTypeFilter([]);
    setPendingZoneFilter([]);
    setAppliedTypeFilter([]);
    setAppliedZoneFilter([]);
  };

  const onMasterCheckboxChange = (e) => {
    const checked = e.checked;
    
    // Get the CountyCodes of filtered rows
    const filteredCountyCodes = filteredData.map(row => row.CountyCode);
    
    // Update only the filtered rows
    const updatedData = drivingTime.map((row) => {
      if (filteredCountyCodes.includes(row.CountyCode)) {
        return { ...row, selected: checked };
      }
      return row;
    });
    
    setdrivingTimeData(updatedData);
  };

  const onRowCheckboxChange = (e, rowData) => {
    const updatedData = drivingTime.map((row) =>
      row.CountyCode === rowData.CountyCode
        ? { ...row, selected: e.checked }
        : row
    );
    setdrivingTimeData(updatedData);
  };

  // Extract distinct values for Type and Zone
  const typeOptions = useMemo(() => {
    return [...new Set(drivingTime.map((item) => item.Type))].map(
      (type) => ({ label: type, value: type })
    );
  }, [drivingTime]);

  const zoneOptions = useMemo(() => {
    return [...new Set(drivingTime.map((item) => item.Zone))].map(
      (zone) => ({ label: zone, value: zone })
    );
  }, [drivingTime]);

  // Filter data based on applied filters (supports multiple selections)
  const filteredData = useMemo(() => {
    let filtered = [...drivingTime];
    
    if (appliedTypeFilter && appliedTypeFilter.length > 0) {
      filtered = filtered.filter(item => appliedTypeFilter.includes(item.Type));
    }
    
    if (appliedZoneFilter && appliedZoneFilter.length > 0) {
      filtered = filtered.filter(item => appliedZoneFilter.includes(item.Zone));
    }
    
    return filtered;
  }, [drivingTime, appliedTypeFilter, appliedZoneFilter]);

  // Calculate master checkbox state based on filtered data
  const isMasterCheckboxChecked = useMemo(() => {
    if (filteredData.length === 0) return false;
    return filteredData.every(row => row.selected);
  }, [filteredData]);

  const onCellEditComplete = (e) => {
    let { rowData, newValue, field } = e;
    
    // Validate that the new value is not null, undefined, or empty
    if (newValue === null || newValue === undefined || newValue === '' || newValue < 0) {
      toast.current.show({
        severity: 'error',
        summary: 'Invalid Value',
        detail: 'Please enter a valid number greater than or equal to 0',
        life: 3000
      });
      return; // Don't update the data if validation fails
    }
    
    let updatedData = [...drivingTime];
    
    // Check if there are any selected rows
    const hasSelectedRows = updatedData.some(row => row.selected);
    
    if (hasSelectedRows) {
      // Update all selected rows with the new value
      updatedData = updatedData.map((row) =>
        row.selected ? { ...row, [field]: newValue } : row
      );
    } else {
      // Update only the current row if no rows are selected
      let index = updatedData.findIndex(
        (d) => d.CountyCode === rowData.CountyCode
      );
      updatedData[index][field] = newValue;
    }
    
    setdrivingTimeData(updatedData);
  };

  const inputNumberEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={(e) => options.editorCallback(e.value)}
        mode="decimal"
        min={0}
        required
        placeholder="Enter a number"
      />
    );
  };

  const onSave = async () => {
    const TimeAndDistance = drivingTime.map((item) => ({
      tme_drve: item.DrivingTime,
      distance: item.Distance,
      minproviders: item.MinimumProviders,
      idn_criteria_spec: item.idn_criteria_spec,
      criteria_id: criteriaId,
    }));

    const session = await fetchAuthSession();
    const token = session.tokens.idToken;
    
    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "BulkUpdateTimeAndDistance",
          TimeAndDistance: TimeAndDistance,
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Minimum Provider Details have been saved successfully.",
          life: 3000,
        });
        fetchDrivingTimeData();
      })
      .catch((error) => {
        console.error("Error saving minimum provider details", error);
        toast.current.show({
          severity: "error",
          summary: "Failure",
          detail: "Error saving minimum provider details. Please try again.",
          life: 3000,
        });
      });
  };

  const handleSave = () => {
    confirmDialog({
      message: "Do you wish to save the minimum provider details?",
      header: "Save Confirmation",
      icon: "pi pi-exclamation-circle",
      accept: () => onSave(),
      reject: () => {},
    });
  };

  return (
    <MainLayout>
      <Toast ref={toast} position="top-right" />
      <div className="edit-minimum-providers">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "1rem 0rem",
          }}
        >
          <Card
            title={`Edit Requirements - ${criteriaName.toUpperCase()}`.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
            style={{ border: "none", boxShadow: "none" }}
          />
          <Button
            label="Save"
            icon="pi pi-save"
            className="p-button-primary"
            severity="success"
            size="small"
            onClick={handleSave}
          />
        </div>
        <DataTable
          value={filteredData}
          editMode="cell"
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 15]}
          loading={isLoading}
          emptyMessage="An error occurred while fetching data."
        >
          <Column
            field="CountyCode"
            header="County Code"
            body={(rowData) => rowData.CountyCode}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "10%" }}
          />
          <Column
            field="County"
            header="County"
            body={(rowData) => rowData.County}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "15%" }}
          />
          <Column
            field="Type"
            header="Type"
            body={(rowData) => rowData.Type}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "12%" }}
            filter
            showFilterMatchModes={false}
            showApplyButton={false}
            showClearButton={false}
            showAddButton={false}
            showFilterOperator={false}
            filterElement={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem' }}>
                <MultiSelect
                  value={pendingTypeFilter}
                  options={typeOptions}
                  onChange={(e) => setPendingTypeFilter(e.value)}
                  placeholder="Select Type(s)"
                  display="chip"
                  maxSelectedLabels={2}
                  style={{ minWidth: '200px' }}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    label="Apply"
                    icon="pi pi-check"
                    size="small"
                    onClick={handleApplyFilters}
                    style={{ flex: 1 }}
                  />
                  <Button
                    label="Clear"
                    icon="pi pi-times"
                    size="small"
                    severity="secondary"
                    onClick={handleClearFilters}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            }
          />
          {/* <Column
            field="Zone"
            header="Zone"
            body={(rowData) => rowData.Zone}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "12%" }}
            filter
            filterElement={
              <Dropdown
                value={zoneFilter}
                options={zoneOptions}
                onChange={(e) => setZoneFilter(e.value)}
                placeholder="Select Zone"
                showClear
              />
            }
          /> */}
          <Column
            field="MinimumProviders"
            header="Minimum Providers"
            editor={(options) => inputNumberEditor(options)}
            onCellEditComplete={onCellEditComplete}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "15%" }}
            body={(rowData) => (
              <div style={{
                border: '2px solid #6c757d',
                borderRadius: '4px',
                padding: '4px 20px',
                backgroundColor: '#f8f9fa',
                display: 'inline-block',
                minWidth: '40px',
                textAlign: 'center'
              }}>
                {rowData.MinimumProviders}
              </div>
            )}
          />
          <Column
            field="DrivingTime"
            header="Time (mins)"
            editor={(options) => inputNumberEditor(options)}
            onCellEditComplete={onCellEditComplete}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "15%" }}
            body={(rowData) => (
              <div style={{
                border: '2px solid #6c757d',
                borderRadius: '4px',
                padding: '4px 20px',
                backgroundColor: '#f8f9fa',
                display: 'inline-block',
                minWidth: '40px',
                textAlign: 'center'
              }}>
                {rowData.DrivingTime}
              </div>
            )}
          />
          <Column
            field="Distance"
            header="Distance (mi)"
            editor={(options) => inputNumberEditor(options)}
            onCellEditComplete={onCellEditComplete}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "15%" }}
            body={(rowData) => (
              <div style={{
                border: '2px solid #6c757d',
                borderRadius: '4px',
                padding: '4px 20px',
                backgroundColor: '#f8f9fa',
                display: 'inline-block',
                minWidth: '40px',
                textAlign: 'center'
              }}>
                {rowData.Distance}
              </div>
            )}
          />
          <Column
            header={
              <Checkbox
                checked={isMasterCheckboxChecked}
                onChange={onMasterCheckboxChange}
              />
            }
            body={(rowData) => (
              <Checkbox
                checked={rowData.selected}
                onChange={(e) => onRowCheckboxChange(e, rowData)}
              />
            )}
            style={{ width: "5%" }}
          />
        </DataTable>
        <ConfirmDialog />
      </div>
    </MainLayout>
  );
};

export default EditMinimumProviders;
